import React, { useEffect } from "react";
import { Palmtree, Briefcase, Clock, ChevronRight, ArrowRight, ArrowRightIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { startOfWeek, endOfWeek, eachDayOfInterval, format } from "date-fns";
import { useSelector } from "react-redux";
import { getTimeOffRequestList } from "../../redux/features/timeoff";
import { useAppDispatch, RootState } from "../../redux/store";
import { DateTimeUtil } from "../../utils";
import { setSelectedTimeoffForView } from "../../redux/features/timeoff/slice";
import { Timeoff } from "../../types";

const WeeklyTimeoffTile: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const user = useSelector((state: RootState) => state.user.user);
    const { isLoadingTimeOffRequests, fetchedTimeoffRequests } = useSelector((state: RootState) => state.timeoff);

    const getIcon = (type: string) => {
        switch (type) {
            case "Vacation":
                return <Palmtree className="w-6 h-6 text-green-500" />;
            case "Sick":
                return <Briefcase className="w-6 h-6 text-red-500" />;
            case "Comp/In Lieu":
                return <Clock className="w-6 h-6 text-blue-500" />;
            default:
                return <Clock className="w-6 h-6 text-gray-500" />;
        }
    };

    const navToTimeoffScreen = () => {
        // navigate("/m-timeoff");
    };

    const getCurrentWeekDates = (): string[] => {
        const today = new Date();
        const weekStart = startOfWeek(today, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(today, { weekStartsOn: 1 });

        return eachDayOfInterval({ start: weekStart, end: weekEnd }).map((date) => format(date, "yyyy-MM-dd"));
    };

    useEffect(() => {
        const currentWeek = getCurrentWeekDates();

        const requestBody = {
            start_date: currentWeek[0],
            end_date: currentWeek[currentWeek.length - 1],
            employee_id: user.id!.toString(),
            category_code: "",
        };

        dispatch(getTimeOffRequestList(requestBody));
    }, [dispatch, user]);

    const currentWeekDates = getCurrentWeekDates();

    if (isLoadingTimeOffRequests) {
        return (
            <div className="flex justify-center items-center h-24">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-blue-500"></div>
            </div>
        );
    }

    if (!Array.isArray(fetchedTimeoffRequests) || fetchedTimeoffRequests.length === 0) {
        return (
            <div className="w-full border border-gray-300 bg-white shadow-lg rounded-lg p-6">
                <h2 className="text-lg font-semibold text-blue-600 text-center">No time off data available</h2>
            </div>
        );
    }

    const navToTimeoffDetails = (timeoff: Timeoff | undefined) => {
        dispatch(setSelectedTimeoffForView(timeoff));
        navigate("/time-off-request-v2/details");
    };

    const navToViewAllTimeoff = () => {
        navigate("/view-all-timeoff-requests");
    };

    return (
        <div className="mt-2 w-full bg-white rounded-lg p-6 max-h-[64vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold text-blue-600 cursor-pointer hover:underline" onClick={navToTimeoffScreen}>
                    Upcoming Time Off
                </h2>
                <button className="flex items-center text-gray-500 hover:text-gray-700 transition" onClick={navToViewAllTimeoff}>
                    <span className="text-sm font-medium">View All</span>
                    <ArrowRight className="w-5 h-5 ml-1" />
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 bg-white rounded-lg">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="py-2 px-4 text-left text-gray-700 font-medium">Date</th>
                            <th className="py-2 px-4 text-left text-gray-700 font-medium">Time Off</th>
                            <th className="py-2 px-4 text-left text-gray-700 font-medium">Hours</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentWeekDates.map((date, index) => {
                            const timeOff = fetchedTimeoffRequests.find((request: any) => request.start_date === date);
                            const isToday = DateTimeUtil.isSameDayWithDateFns(new Date(date), new Date());

                            return (
                                <tr
                                    key={index}
                                    className={`border-t ${isToday ? "bg-blue-50 border-blue-300" : "border-gray-200"}`}
                                    onClick={() => timeOff && navToTimeoffDetails(timeOff)}
                                >
                                    <td className={`py-2 px-4 font-medium ${isToday ? "text-blue-600" : "text-gray-700"}`}>
                                        {DateTimeUtil.formatDate(new Date(date), "MMM dd")}, {DateTimeUtil.getAbbreviatedDayName(new Date(date))}
                                    </td>
                                    {timeOff ? (
                                        <>
                                            <td className="py-2 px-4 flex items-center space-x-2">
                                                {getIcon(timeOff.category_code!)}
                                                <span className="text-gray-700">{timeOff.category_code}</span>
                                            </td>
                                            <td className="py-2 px-4 text-gray-700">{timeOff.hours_requested}h</td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="py-2 px-4 text-gray-500 flex items-center">
                                                <Clock className="w-6 h-6 text-gray-500 mr-2" />
                                                No time off
                                            </td>
                                            <td className="py-2 px-4 text-gray-700">-</td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default WeeklyTimeoffTile;
